import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen, Plus, Trash, DownloadSimple, UploadSimple, FloppyDisk, XCircle,
  FileArrowUp, Spinner, CheckCircle, Warning,
  PlayCircle, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { uploadOmrBatch } from "../lib/omrUpload";
import ExamWorkspace from "./ExamWorkspace";
import ExamBookletsDialog, { BookletNavigation, bookletRows } from "../components/ExamBooklets";
import ExamQuestionBuilder from "./ExamQuestionBuilder";
import ExamCopyDialog from "../components/ExamCopyDialog";
import ExamQuickPublishButton from "../components/ExamQuickPublishButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const DEFAULT_LETTERS = ["A", "B", "C", "D"];
const HISTORICAL_EXAM_TEMPLATE_ENDPOINT = "/historical-exams/template.xlsx";
const HISTORICAL_EXAM_IMPORT_ENDPOINT = "/historical-exams/import";
const todayInputValue = () => {
  const value = new Date();
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const normalizeKeySelection = (value, optionLabels = DEFAULT_LETTERS) => {
  if (Array.isArray(value)) return value.filter((item, idx, arr) => optionLabels.includes(item) && arr.indexOf(item) === idx);
  if (typeof value === "string") {
    return value.toUpperCase().split(/[^A-Z]+/).filter((item, idx, arr) => optionLabels.includes(item) && arr.indexOf(item) === idx);
  }
  return [];
};

const serializeKeySelection = (list) => {
  if (!list?.length) return null;
  return list.length === 1 ? list[0] : list;
};

/* ---------- Answer grid editor ---------- */
function GabaritoEditor({ answers, annulled, onChange, questionCount = 100, optionLabels = DEFAULT_LETTERS, preserveAnswerWhenAnnulled = false }) {
  const [choiceDialog, setChoiceDialog] = useState(null);
  const isAnnulled = (q) => annulled.includes(q);

  const applySelection = (q, selection) => {
    const next = { ...answers };
    next[String(q)] = serializeKeySelection(selection);
    onChange({ answers: next, annulled });
  };

  const setAnswer = (q, letter) => {
    if (isAnnulled(q)) return;
    const current = normalizeKeySelection(answers[String(q)], optionLabels);
    if (current.includes(letter)) {
      applySelection(q, current.filter((item) => item !== letter));
      return;
    }
    if (current.length >= 1) {
      setChoiceDialog({ q, letter, current });
      return;
    }
    applySelection(q, [letter]);
  };

  const toggleAnnul = (q) => {
    const s = new Set(annulled);
    if (s.has(q)) s.delete(q); else s.add(q);
    const next = { ...answers };
    if (s.has(q) && !preserveAnswerWhenAnnulled) next[String(q)] = null;
    onChange({ answers: next, annulled: Array.from(s).sort((a, b) => a - b) });
  };

  return (
    <>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        {Array.from({ length: questionCount }, (_, i) => i + 1).map((q) => {
          const selected = normalizeKeySelection(answers[String(q)], optionLabels);
          const annul = isAnnulled(q);
          return (
            <div
              key={q}
              data-testid={`gabarito-q${q}`}
              className={`border p-2 transition-colors ${annul ? "border-destructive bg-destructive/5" : "border-border"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="question-number">Q{String(q).padStart(2, "0")}</span>
                <button
                  data-testid={`gabarito-q${q}-annul`}
                  onClick={() => toggleAnnul(q)}
                  title="Anular"
                  className={`text-[10px] font-mono-data px-1 border ${annul ? "border-destructive text-destructive bg-card" : "border-border text-muted-foreground hover:text-destructive hover:border-destructive"}`}
                >
                  {annul ? "×" : "∅"}
                </button>
              </div>
              <div className="flex gap-0.5">
                {optionLabels.map((l) => (
                  <button
                    key={l}
                    data-testid={`gabarito-q${q}-${l}`}
                    disabled={annul}
                    onClick={() => setAnswer(q, l)}
                    className={`flex-1 text-xs font-mono-data font-semibold border transition-colors ${
                      annul
                        ? "border-border text-muted-foreground cursor-not-allowed"
                        : selected.includes(l)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-foreground hover:border-foreground"
                    }`}
                    style={{ minHeight: "22px" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={Boolean(choiceDialog)} onOpenChange={(open) => !open && setChoiceDialog(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              Questão já possui outra alternativa
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esta questão já tem alternativa marcada no gabarito. Deseja manter ambas ou substituir pela nova alternativa?
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setChoiceDialog(null)} className="rounded-none">Cancelar</Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!choiceDialog) return;
                applySelection(choiceDialog.q, [...choiceDialog.current, choiceDialog.letter]);
                setChoiceDialog(null);
              }}
              className="rounded-none"
            >
              Manter ambas
            </Button>
            <Button
              onClick={() => {
                if (!choiceDialog) return;
                applySelection(choiceDialog.q, [choiceDialog.letter]);
                setChoiceDialog(null);
              }}
              className="rounded-none"
            >
              Substituir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ---------- Exam detail (editable) ---------- */
function ExamDetail({ exam, layouts = [], classes = [], semesters = [], stages = [], disciplines = [], onClose, onChange }) {
  const canEditImportedKey = Boolean(exam.can_edit_imported_key);
  const canEditQuestionBankKey = Boolean(exam.can_edit_question_bank_key || canEditImportedKey);
  const onlineOnly = exam.delivery_mode === "online_only";
  const [draft, setDraft] = useState({
    name: exam.name,
    exam_date: exam.exam_date || "",
    semester_id: exam.semester_id || "",
    stage_id: exam.stage_id || "",
    academic_scope: exam.academic_scope || (exam.stage_id ? "global" : "legacy"),
    discipline_id: exam.discipline_id || "",
    answers: { ...exam.answers },
    annulled: [...(exam.annulled || [])],
    question_count: exam.question_count || 100,
    perception_count: exam.perception_count ?? 9,
    perception_questions: [...(exam.perception_questions || [])],
    layout_id: exam.layout_id || "saki-oficial-a4-100",
    share_with_discipline_teachers: Boolean(exam.share_with_discipline_teachers),
    shared_teacher_ids: [...(exam.shared_teacher_ids || [])],
    shared_access_level: exam.shared_access_level || "exam",
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const originalDraft = useRef(draft);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = exam.question_source === "question_bank"
        ? {
          name: draft.name,
          exam_date: draft.exam_date,
          semester_id: draft.semester_id,
          stage_id: draft.stage_id || null,
          academic_scope: draft.stage_id ? draft.academic_scope : "legacy",
          discipline_id: draft.stage_id && draft.academic_scope === "discipline" ? draft.discipline_id : null,
          question_count: draft.question_count,
          ...(!onlineOnly ? {
            layout_id: draft.layout_id,
            perception_count: draft.perception_count,
            perception_questions: draft.perception_questions,
          } : {}),
          share_with_discipline_teachers: draft.share_with_discipline_teachers,
          shared_teacher_ids: draft.shared_teacher_ids,
          shared_access_level: draft.shared_access_level,
          ...(canEditQuestionBankKey ? { answers: draft.answers, annulled: draft.annulled } : {}),
        }
        : draft;
      const changed = Object.fromEntries(Object.entries(payload).filter(([key, value]) => (
        JSON.stringify(value ?? "") !== JSON.stringify(originalDraft.current[key] ?? "")
      )));
      const res = await api.put(`/exams/${exam.id}`, changed);
      toast.success(res.data.booklet_keys_updated ? `Gabarito atualizado nos ${res.data.booklet_keys_updated} cadernos` : canEditQuestionBankKey
        ? `Gabarito atualizado${res.data.question_versions_updated ? ` · ${res.data.question_versions_updated} questão(ões) sincronizada(s)` : ""}`
        : "Prova salva");
      onChange(res.data);
      onClose();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  const doImport = async (file) => {
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/exams/${exam.id}/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, annulled, perception_imported: perceptionImported, errors } = res.data;
      toast.success(`Importado: ${imported} respostas · ${annulled} anuladas · ${perceptionImported || 0} perguntas de percepção`);
      if (errors?.length) errors.slice(0, 3).forEach(er => toast.error(er));
      const fresh = res.data.exam;
      setDraft({
        name: fresh.name,
        exam_date: fresh.exam_date || "",
        semester_id: fresh.semester_id || "",
        stage_id: fresh.stage_id || "",
        academic_scope: fresh.academic_scope || (fresh.stage_id ? "global" : "legacy"),
        discipline_id: fresh.discipline_id || "",
        answers: { ...fresh.answers },
        annulled: [...(fresh.annulled || [])],
        question_count: fresh.question_count || 100,
        perception_count: fresh.perception_count ?? 9,
        perception_questions: [...(fresh.perception_questions || [])],
        layout_id: fresh.layout_id || "saki-oficial-a4-100",
        share_with_discipline_teachers: Boolean(fresh.share_with_discipline_teachers),
        shared_teacher_ids: [...(fresh.shared_teacher_ids || [])],
        shared_access_level: fresh.shared_access_level || "exam",
      });
      onChange(fresh);
    } catch (e) { toast.error(fmtError(e)); }
    finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const activeLayout = layouts.find((item) => item.id === draft.layout_id)
    || layouts.find((item) => item.is_default)
    || { max_question_count: 100, max_perception_count: 10 };
  const optionLabels = activeLayout.option_labels || DEFAULT_LETTERS;
  const marked = Object.values(draft.answers).filter((value) => normalizeKeySelection(value, optionLabels).length > 0).length;
  const examSchoolIds = Array.from(new Set(
    (exam.class_ids || [exam.class_id]).filter(Boolean)
      .map((id) => classes.find((item) => item.id === id)?.school_id).filter(Boolean),
  ));
  const examSchoolId = examSchoolIds.length === 1 ? examSchoolIds[0] : "";
  const availableSemesters = semesters.filter((item) => !item.school_id || !examSchoolId || item.school_id === examSchoolId);
  const draftClassIds = draft.class_ids || exam.class_ids || (exam.class_id ? [exam.class_id] : []);
  const availableStages = stages.filter((item) => (item.matrix_id || item.semester_id === draft.semester_id)
    && (!examSchoolId || item.school_id === examSchoolId)
    && (!draftClassIds.length || draftClassIds.every((id) => (item.class_ids || []).includes(id))));
  const availableDisciplines = disciplines.filter((item) => item.stage_id === draft.stage_id);

  useEffect(() => {
    if (!exam.can_manage_sharing || !examSchoolId) { setTeacherOptions([]); return; }
    api.get(`/academic-teachers?school_id=${encodeURIComponent(examSchoolId)}`)
      .then((response) => setTeacherOptions(response.data || []))
      .catch(() => setTeacherOptions([]));
  }, [exam.can_manage_sharing, examSchoolId]);

  const toggleSharedTeacher = (teacherId) => {
    setDraft((current) => ({
      ...current,
      shared_teacher_ids: current.shared_teacher_ids.includes(teacherId)
        ? current.shared_teacher_ids.filter((value) => value !== teacherId)
        : [...current.shared_teacher_ids, teacherId],
    }));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="rounded-none max-w-6xl max-h-[90vh] overflow-y-auto" aria-describedby="exam-detail-desc">
        <DialogHeader className="flex flex-row items-center justify-between mr-8">
          <div>
            <DialogTitle className="font-display text-3xl font-black tracking-tight">
              {exam.name}
            </DialogTitle>
            <p id="exam-detail-desc" className="text-xs text-muted-foreground font-mono-data mt-1">
              {marked}/{draft.question_count || 100} respondidas · {draft.annulled.length} anuladas
            </p>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
            <div>
              <div className="label-eyebrow mb-1">nome da prova</div>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="exam-edit-name"
                className="rounded-none font-mono-data"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">data de realização</div>
              <Input type="date" value={draft.exam_date || ""} onChange={(e) => setDraft({ ...draft, exam_date: e.target.value })} data-testid="exam-edit-date" className="rounded-none font-mono-data" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border border-border p-4">
            <div>
              <div className="label-eyebrow mb-1">semestre letivo</div>
              <Select value={draft.semester_id} onValueChange={(value) => setDraft({ ...draft, semester_id: value, stage_id: "", academic_scope: "legacy", discipline_id: "" })}>
                <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{availableSemesters.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">etapa</div>
              <Select value={draft.stage_id || "none"} onValueChange={(value) => setDraft({ ...draft, stage_id: value === "none" ? "" : value, academic_scope: value === "none" ? "legacy" : "global", discipline_id: "" })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sem etapa / legado</SelectItem>{availableStages.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {draft.stage_id && (
              <div>
                <div className="label-eyebrow mb-1">tipo da prova</div>
                <Select value={draft.academic_scope} onValueChange={(value) => setDraft({ ...draft, academic_scope: value, discipline_id: value === "global" ? "" : draft.discipline_id })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="global">Global da etapa / simulado</SelectItem><SelectItem value="discipline">Prova de disciplina</SelectItem></SelectContent>
                </Select>
              </div>
            )}
            {draft.stage_id && draft.academic_scope === "discipline" && (
              <div className="md:col-span-3">
                <div className="label-eyebrow mb-1">disciplina</div>
                <Select value={draft.discipline_id} onValueChange={(value) => setDraft({ ...draft, discipline_id: value })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>{availableDisciplines.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          {exam.can_manage_sharing && (
            <div className="border border-primary/40 bg-primary/5 p-4" data-testid="exam-sharing-panel">
              <div className="font-display text-xl font-black tracking-tight">Compartilhamento da prova</div>
              <p className="text-xs text-muted-foreground mt-1 mb-4">O criador escolhe quem pode acessar e até onde os resultados serão exibidos.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <div className="label-eyebrow mb-1">nível liberado</div>
                    <Select value={draft.shared_access_level} onValueChange={(value) => setDraft({ ...draft, shared_access_level: value })}>
                      <SelectTrigger className="rounded-none" data-testid="exam-sharing-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="exam">Somente a prova</SelectItem>
                        <SelectItem value="global">Prova + resultados globais</SelectItem>
                        <SelectItem value="individual">Prova + resultados individuais</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className={`flex items-start gap-3 border p-3 ${draft.discipline_id ? "border-border cursor-pointer" : "border-border opacity-50"}`}>
                    <input type="checkbox" className="mt-1" disabled={!draft.discipline_id}
                      checked={draft.share_with_discipline_teachers}
                      onChange={(event) => setDraft({ ...draft, share_with_discipline_teachers: event.target.checked })} />
                    <span className="text-sm"><b>Professores da mesma disciplina</b><span className="block text-xs text-muted-foreground">Inclui automaticamente os professores alocados nessa disciplina em 2026.2.</span></span>
                  </label>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">outros professores desta escola</div>
                  <div className="border border-border bg-card max-h-52 overflow-y-auto divide-y divide-border">
                    {teacherOptions.length ? teacherOptions.filter((teacher) => teacher.id !== exam.created_by).map((teacher) => (
                      <label key={teacher.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30">
                        <input type="checkbox" checked={draft.shared_teacher_ids.includes(teacher.id)} onChange={() => toggleSharedTeacher(teacher.id)} />
                        <span className="text-sm"><b>{teacher.name}</b><span className="block text-xs text-muted-foreground">{teacher.email}</span></span>
                      </label>
                    )) : <div className="p-4 text-sm text-muted-foreground">Nenhum outro professor pré-cadastrado nesta escola.</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {onlineOnly ? (
            <div className="border border-primary bg-primary/5 p-4" data-testid="exam-online-only-detail">
              <div className="font-mono-data font-bold text-primary">Aplicação exclusivamente online · {draft.question_count} questões</div>
              <div className="text-sm text-muted-foreground mt-1">Esta prova não usa layout de cartão e não aceita leitura OMR.</div>
            </div>
          ) : <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="label-eyebrow mb-1">layout do cartÃ£o</div>
              <Select
                value={draft.layout_id}
                onValueChange={(value) => {
                  const next = layouts.find((item) => item.id === value) || activeLayout;
                  setDraft({
                    ...draft,
                    layout_id: value,
                    question_count: Math.min(Number(draft.question_count || 1), next.max_question_count),
                    perception_count: Math.min(Number(draft.perception_count || 0), next.max_perception_count),
                  });
                }}
              >
                <SelectTrigger className="rounded-none font-mono-data"><SelectValue /></SelectTrigger>
                <SelectContent>{layouts.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">questões objetivas visíveis</div>
              <Input
                type="number"
                min={1}
                max={activeLayout.max_question_count}
                value={draft.question_count}
                onChange={(e) => setDraft({ ...draft, question_count: Number(e.target.value || 100) })}
                className="rounded-none font-mono-data"
              />
            </div>
            <div>
              <div className="label-eyebrow mb-1">questões de percepção visíveis</div>
              <Input
                type="number"
                min={0}
                max={activeLayout.max_perception_count}
                value={draft.perception_count}
                disabled={activeLayout.max_perception_count === 0}
                onChange={(e) => setDraft({ ...draft, perception_count: Number(e.target.value || 0) })}
                className="rounded-none font-mono-data"
              />
              {activeLayout.max_perception_count === 0 && <div className="text-xs text-muted-foreground mt-1">Este cartão não possui campos físicos de percepção.</div>}
            </div>
          </div>}

          {exam.question_source !== "question_bank" ? <><div className="flex gap-2 pb-2 border-b border-border">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
              data-testid="exam-upload-input"
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              data-testid="exam-upload-btn"
              className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
            >
              <UploadSimple size={14} weight="bold" className="mr-2" />
              {importing ? "Importando…" : "Importar gabarito e percepção"}
            </Button>
            <span className="label-eyebrow ml-2 self-center">
              ou edite manualmente abaixo · clique ∅ para anular · múltiplas corretas são permitidas
            </span>
          </div>

          <GabaritoEditor
            answers={draft.answers}
            annulled={draft.annulled}
            questionCount={draft.question_count || 100}
            optionLabels={optionLabels}
            onChange={(next) => setDraft({ ...draft, ...next })}
          />
          </> : canEditQuestionBankKey ? <>
            <div className="border border-primary/40 bg-primary/5 p-4 text-sm">
              <div className="font-mono-data font-bold text-primary">{canEditImportedKey ? "Gabarito importado por XLSX" : "Gabarito da prova"}</div>
              <div className="text-muted-foreground mt-1">
                {canEditImportedKey
                  ? "Você pode trocar a alternativa correta e anular ou desanular questões. Ao salvar, o gabarito da prova e as questões importadas serão sincronizados; as notas e a psicometria serão recalculadas sem reprocessar os cartões."
                  : "Você pode trocar a alternativa correta e anular ou desanular questões nesta prova. O caderno comentado, as notas e a psicometria serão atualizados sem alterar a questão original do banco."}
              </div>
            </div>
            <GabaritoEditor
              answers={draft.answers}
              annulled={draft.annulled}
              questionCount={draft.question_count || 100}
              optionLabels={optionLabels}
              preserveAnswerWhenAnnulled
              onChange={(next) => setDraft({ ...draft, ...next })}
            />
            <div className="border border-border p-3 text-sm text-muted-foreground space-y-3">
              <div>Para atualizar enunciados, classificações ou perguntas de percepção em massa, importe novamente o modelo administrativo.</div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
              />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
                <UploadSimple size={14} weight="bold" className="mr-2" />
                {importing ? "Importando…" : "Importar percepção do XLSX"}
              </Button>
            </div>
          </> : <div className="border border-primary/40 bg-primary/5 p-4 text-sm text-muted-foreground space-y-3">
            <div>O gabarito desta prova é derivado do Banco de Questões. Você ainda pode importar os enunciados de percepção pela aba <b>Percepcao</b> do XLSX.</div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
              <UploadSimple size={14} weight="bold" className="mr-2" />
              {importing ? "Importando…" : "Importar percepção do XLSX"}
            </Button>
          </div>}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="rounded-none">
            <XCircle size={14} weight="bold" className="mr-2" />Fechar
          </Button>
          <Button onClick={save} disabled={saving} data-testid="exam-save"
            className="rounded-none font-mono-data uppercase tracking-widest text-xs">
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Salvando…" : "Salvar prova"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenericCorrectionDialog({ open, onOpenChange }) {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const [reviewReport, setReviewReport] = useState(null);

  const onDrop = useCallback(async (accepted) => {
    if (!accepted.length || processing) return;
    setProcessing(true);
    setProgress(0);
    setBatchResults([]);
    setReviewReport(null);
    let jobId = null;
    try {
      const response = await uploadOmrBatch(accepted, { onProgress: setProgress });
      jobId = response.data.job_id;
      if (!jobId) throw new Error("Servidor não retornou o identificador do processamento");
    } catch (error) {
      toast.error("Falha no envio: " + fmtError(error));
      setProcessing(false);
      return;
    }

    setProgress(0);
    try {
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 1800));
        const response = await api.get(`/omr/jobs/${jobId}`);
        const job = response.data;
        const successful = job.results || [];
        const errors = (job.errors || []).map((item) => ({ filename: item.filename, error: item.error }));
        setBatchResults([...successful, ...errors]);
        const denominator = Math.max(job.total_pages || job.total_files || 1, 1);
        setProgress(Math.min(100, Math.round(((job.processed_pages || 0) / denominator) * 100)));
        if (job.status === "completed" || job.status === "failed") {
          if (job.status === "failed") {
            toast.error("Processamento interrompido: " + (job.error || "erro desconhecido"));
          } else {
            toast.success(`${successful.length} cartão(ões) processado(s)`);
            setReviewReport(job.review_report || { counts: {} });
          }
          break;
        }
      }
    } catch (error) {
      toast.error("Falha ao acompanhar o processamento: " + fmtError(error));
    } finally {
      setProcessing(false);
    }
  }, [processing]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
    },
    multiple: true,
    disabled: processing,
  });

  const totalReview = Object.values(reviewReport?.counts || {}).reduce((sum, value) => sum + Number(value || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" aria-describedby="mixed-correction-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">Correção geral por QR</DialogTitle>
        </DialogHeader>
        <div id="mixed-correction-desc" className="text-sm text-muted-foreground">
          Envie cartões de provas diferentes no mesmo lote. Quando houver QR reconhecido, o sistema separa automaticamente pelas respectivas provas.
        </div>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed p-10 text-center transition-colors ${
            processing
              ? "border-border bg-muted/20 cursor-not-allowed opacity-70"
              : isDragActive
                ? "border-primary bg-primary/5 cursor-copy"
                : "border-border hover:border-primary cursor-pointer"
          }`}
        >
          <input {...getInputProps()} />
          {processing ? (
            <Spinner size={52} className="mx-auto text-primary animate-spin" />
          ) : (
            <FileArrowUp size={52} weight="duotone" className="mx-auto text-primary" />
          )}
          <div className="font-display text-2xl font-black tracking-tight mt-4">
            {processing ? "Processando cartões…" : "Envie cartões de provas diferentes"}
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            PDFs e imagens são aceitos. Sem QR ou com QR inválido, a correção fica disponível para alocação manual.
          </div>
        </div>

        {processing && (
          <div className="border border-border bg-card p-4">
            <div className="flex justify-between text-xs font-mono-data mb-2"><span>processamento OMR</span><span>{progress}%</span></div>
            <div className="h-2 bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        {!!reviewReport?.routed_summary?.length && (
          <section className="border border-border bg-card">
            <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Provas identificadas no lote</div>
            <div className="p-4 flex flex-wrap gap-2">
              {reviewReport.routed_summary.map((item) => (
                <span key={item.exam_name} className="border border-border px-3 py-1 text-xs font-mono-data">
                  {item.exam_name} · {item.count}
                </span>
              ))}
            </div>
          </section>
        )}

        {!!reviewReport?.exam_issues?.length && (
          <section className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5">
            <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Prova não identificada automaticamente</div>
            <div className="divide-y divide-border">
              {reviewReport.exam_issues.map((item) => (
                <div key={item.correction_id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="font-semibold">{item.filename}</div>
                    <div className="text-xs text-muted-foreground font-mono-data mt-1">CPF {item.cpf || "não reconhecido"}</div>
                    <div className="text-sm text-muted-foreground mt-1">{item.message}</div>
                  </div>
                  <a href={`/correction/${item.correction_id}`} className="text-xs font-mono-data uppercase tracking-widest text-primary hover:underline">Abrir correção</a>
                </div>
              ))}
            </div>
          </section>
        )}

        {batchResults.length > 0 && (
          <section className="border border-border bg-card">
            <div className="px-4 py-3 border-b border-border font-mono-data font-bold">Último lote</div>
            <div className="divide-y divide-border">
              {batchResults.map((result, index) => (
                <div key={`${result.id || result.filename}-${index}`} className="px-4 py-3 flex items-center gap-3">
                  {result.error ? (
                    <XCircle size={18} weight="fill" className="text-destructive shrink-0" />
                  ) : result.exam_resolution_issue || result.duplicate_status === "pending" ? (
                    <Warning size={18} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
                  ) : (
                    <CheckCircle size={18} weight="fill" className="text-[hsl(var(--success))] shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{result.filename}</div>
                    <div className="text-xs text-muted-foreground font-mono-data">
                      {result.error ? result.error : `CPF ${result.cpf || "não reconhecido"}`}
                      {result.exam_resolution_issue ? ` · ${result.exam_resolution_issue}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Fechar</Button>
          {totalReview > 0 && (
            <div className="text-xs text-muted-foreground font-mono-data self-center">
              {totalReview} pendência(s) de revisão neste lote
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Historical exam import ---------- */
function HistoricalExamImportDialog({
  open,
  onOpenChange,
  classes = [],
  semesters = [],
  layouts = [],
  onImported,
}) {
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [classIds, setClassIds] = useState([]);
  const [layoutId, setLayoutId] = useState("");
  const [periodMode, setPeriodMode] = useState("new");
  const [semesterId, setSemesterId] = useState("");
  const [semesterName, setSemesterName] = useState("");
  const [examName, setExamName] = useState("");
  const [examDate, setExamDate] = useState("");
  const [questionCount, setQuestionCount] = useState(100);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [fileDragging, setFileDragging] = useState(false);
  const importWarnings = Array.isArray(result?.warnings) ? result.warnings : [];
  const importWarningCount = Number(result?.warning_count || importWarnings.length || 0);
  const historicalLayoutClassification = result?.historical_layout_classification || null;
  const historicalClassificationIsWarning = historicalLayoutClassification?.level === "warning";

  const activeLayout = layouts.find((item) => item.id === layoutId)
    || layouts.find((item) => item.is_default)
    || layouts[0]
    || null;
  const layoutQuestionCapacity = Math.max(1, Math.min(200, Number(activeLayout?.max_question_count || 200)));
  const exceedsLayoutCapacity = questionCount > layoutQuestionCapacity;

  useEffect(() => {
    if (!layoutId && activeLayout?.id) {
      setLayoutId(activeLayout.id);
    }
  }, [layoutId, activeLayout?.id]);

  const toggleClass = (id) => {
    setClassIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const changeLayout = (value) => {
    const nextLayout = layouts.find((item) => item.id === value);
    setLayoutId(value);
    // Fixed external cards define their real physical count.  Preselect it
    // so choosing the 120-objective Teste de Progresso profile immediately
    // produces the matching historical XLSX instead of retaining 100.
    if (nextLayout?.fixed_question_count) {
      setQuestionCount(Number(nextLayout.default_question_count || nextLayout.max_question_count || 100));
    }
  };

  const reset = () => {
    setClassIds([]);
    setLayoutId("");
    setPeriodMode("new");
    setSemesterId("");
    setSemesterName("");
    setExamName("");
    setExamDate("");
    setQuestionCount(100);
    setResult(null);
    setErrors([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    if (uploading) return;
    reset();
    onOpenChange(false);
  };

  const openChanged = (value) => {
    if (!value) close();
  };

  const historicalSemesters = semesters.filter((item) => {
    const status = String(item.semester_status || "").toLowerCase();
    return status === "history" || status === "historical" || item.is_historical;
  });
  const semesterOptions = historicalSemesters;

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const selectedLayoutId = activeLayout?.id || layoutId || "saki-oficial-a4-100";
      const response = await api.get(`${HISTORICAL_EXAM_TEMPLATE_ENDPOINT}?question_count=${encodeURIComponent(questionCount || 100)}&layout_id=${encodeURIComponent(selectedLayoutId)}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "modelo_importacao_prova_historica.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("Modelo histórico baixado");
    } catch (error) {
      toast.error("Falha ao baixar o modelo: " + fmtError(error));
    } finally {
      setDownloading(false);
    }
  };

  const upload = async (file) => {
    if (!file || uploading) return;
    if (!classIds.length) {
      toast.error("Selecione ao menos uma turma desta prova histórica");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!examName.trim()) {
      toast.error("Informe o nome da prova histórica");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!examDate) {
      toast.error("Informe a data de realização");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (periodMode === "existing" && !semesterId) {
      toast.error("Selecione o semestre histórico");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (periodMode === "new" && !semesterName.trim()) {
      toast.error("Informe o ano ou semestre histórico que será criado");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    setResult(null);
    setErrors([]);
    try {
      const body = new FormData();
      body.append("file", file);
      // Keep class_id for installations that have not yet received the
      // multi-class importer while the current backend consumes class_ids.
      body.append("class_id", classIds[0]);
      body.append("class_ids", JSON.stringify(classIds));
      body.append("exam_name", examName.trim());
      body.append("exam_date", examDate);
      body.append("question_count", String(questionCount || 100));
      body.append("layout_id", activeLayout?.id || layoutId || "saki-oficial-a4-100");
      body.append("answer_sheet_layout_id", activeLayout?.id || layoutId || "saki-oficial-a4-100");
      body.append("historical_import", "true");
      if (periodMode === "existing") body.append("semester_id", semesterId);
      else body.append("semester_name", semesterName.trim());

      const response = await api.post(HISTORICAL_EXAM_IMPORT_ENDPOINT, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      toast.success(`${response.data.question_count || 0} questão(ões) e ${response.data.result_count || 0} resultado(s) históricos importados`);
      onImported?.(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setErrors(detail.errors);
        toast.error(`${detail.error_count || detail.errors.length} erro(s): a prova não foi importada`);
      } else {
        toast.error("Falha na importação histórica: " + fmtError(error));
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const receiveSpreadsheet = (file) => {
    if (!file || uploading) return;
    if (!/\.xlsx$/i.test(String(file.name || ""))) {
      toast.error("Envie uma planilha XLSX (.xlsx)");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    upload(file);
  };

  return (
    <Dialog open={open} onOpenChange={openChanged}>
      <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto overscroll-contain" aria-describedby="historical-exam-import-desc">
        <DialogHeader>
          <div className="label-eyebrow mb-2">importação · histórico acadêmico</div>
          <DialogTitle className="font-display text-3xl font-black tracking-tight">Importar prova histórica</DialogTitle>
        </DialogHeader>
        <p id="historical-exam-import-desc" className="text-sm text-muted-foreground">
          Registre provas já realizadas. A planilha contém as questões, alternativas, gabarito e uma aba de resultados para vincular as notas ao histórico de cada aluno.
        </p>

        <div className="border border-primary/40 bg-primary/5 p-4 text-sm">
          <div className="font-mono-data font-bold text-primary">Como funciona</div>
          <ol className="mt-2 list-decimal pl-5 space-y-1 text-muted-foreground">
            <li>Baixe o modelo com o número de questões desejado.</li>
            <li>Preencha as abas <b>Questões</b> e <b>Resultados</b>.</li>
            <li>Escolha uma ou mais turmas, o tipo de gabarito e o período passado. Se ele ainda não existir, o SAKI/Xm o cria como histórico.</li>
          </ol>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-eyebrow mb-1">nome da prova</div>
            <Input value={examName} onChange={(event) => setExamName(event.target.value)} placeholder="Ex.: Prova de Clínica 2023" className="rounded-none" data-testid="historical-exam-name" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">data de realização</div>
            <Input type="date" value={examDate} onChange={(event) => setExamDate(event.target.value)} className="rounded-none font-mono-data" data-testid="historical-exam-date" />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="label-eyebrow">turmas da prova</div>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" className="h-auto px-1 text-xs" onClick={() => setClassIds(classes.map((item) => item.id))} disabled={!classes.length || uploading}>Todas</Button>
                <Button type="button" variant="ghost" size="sm" className="h-auto px-1 text-xs" onClick={() => setClassIds([])} disabled={!classIds.length || uploading}>Limpar</Button>
              </div>
            </div>
            <div className="border border-border p-3 grid gap-2 max-h-44 overflow-y-auto" data-testid="historical-exam-class">
              {classes.map((item) => (
                <label key={item.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={classIds.includes(item.id)} onChange={() => toggleClass(item.id)} disabled={uploading} data-testid={`historical-exam-class-${item.id}`} />
                  <span>{item.name}</span>
                </label>
              ))}
              {!classes.length && <div className="text-xs text-muted-foreground">Nenhuma turma disponível para sua permissão.</div>}
            </div>
            <div className="text-xs text-muted-foreground mt-1 font-mono-data">{classIds.length} turma(s) selecionada(s)</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">número de questões</div>
            <Input type="number" min={1} max={200} value={questionCount} onChange={(event) => setQuestionCount(Math.max(1, Math.min(200, Number(event.target.value || 1))))} className="rounded-none font-mono-data" data-testid="historical-exam-question-count" />
            <div className="text-xs text-muted-foreground mt-1">A importação histórica aceita de 1 a 200 questões.</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">tipo de gabarito / cartão</div>
            <Select value={activeLayout?.id || undefined} onValueChange={changeLayout} disabled={!layouts.length || uploading}>
              <SelectTrigger className="rounded-none" data-testid="historical-exam-layout"><SelectValue placeholder="Selecione o tipo..." /></SelectTrigger>
              <SelectContent>{layouts.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Capacidade física/OMR: {layoutQuestionCapacity} questões.{activeLayout?.description ? ` ${activeLayout.description}` : ""}</div>
          </div>
        </div>

        {exceedsLayoutCapacity && (
          <div role="alert" className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-4 flex items-start gap-3" data-testid="historical-layout-capacity-warning">
            <Warning size={22} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-mono-data font-bold">Importação sem cartão físico ou leitura por scanner</div>
              <p className="text-muted-foreground mt-1">
                Você selecionou {questionCount} questões, mas {activeLayout?.name || "o cartão selecionado"} comporta apenas {layoutQuestionCapacity}. O XLSX histórico continua importável e os resultados entram no desempenho dos alunos; apenas não haverá cartão imprimível nem correção OMR para esta prova.
              </p>
            </div>
          </div>
        )}

        <section className="border border-border p-4">
          <div className="font-mono-data font-bold text-sm">Período letivo histórico</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <button type="button" onClick={() => setPeriodMode("new")}
              className={`border p-3 text-left ${periodMode === "new" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="historical-period-new">
              <div className="font-semibold text-sm">Criar período passado</div>
              <div className="text-xs text-muted-foreground mt-1">Ex.: 2023.2, 2022.1 ou 2021.</div>
            </button>
            <button type="button" onClick={() => setPeriodMode("existing")}
              className={`border p-3 text-left ${periodMode === "existing" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="historical-period-existing">
              <div className="font-semibold text-sm">Usar período já cadastrado</div>
              <div className="text-xs text-muted-foreground mt-1">Vincula a prova a um semestre histórico existente.</div>
            </button>
          </div>
          {periodMode === "new" ? (
            <div className="mt-3">
              <div className="label-eyebrow mb-1">ano ou semestre passado</div>
              <Input value={semesterName} onChange={(event) => setSemesterName(event.target.value)} placeholder="Ex.: 2023.2 ou 2021" className="rounded-none font-mono-data" data-testid="historical-semester-name" />
            </div>
          ) : (
            <div className="mt-3">
              <div className="label-eyebrow mb-1">semestre histórico</div>
              <Select value={semesterId} onValueChange={setSemesterId}>
                <SelectTrigger className="rounded-none" data-testid="historical-semester-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {semesterOptions.length ? semesterOptions.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">Ainda não há período histórico cadastrado.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </section>

        <div className="border border-border bg-card p-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div>
            <div className="font-mono-data font-bold text-sm">Planilha de prova histórica</div>
            <div className="text-xs text-muted-foreground mt-1">Formato XLSX · questões, alternativas, gabarito e resultados por CPF.</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={downloadTemplate} disabled={downloading || uploading}
              className="rounded-none font-mono-data uppercase tracking-widest text-xs" data-testid="historical-exam-template-download">
              <DownloadSimple size={14} weight="bold" className="mr-2" />
              {downloading ? "Gerando…" : "Baixar modelo XLSX"}
            </Button>
          </div>
        </div>

        <label
          htmlFor="historical-exam-upload-input"
          data-testid="historical-exam-upload-dropzone"
          onDragOver={(event) => { event.preventDefault(); if (!uploading) setFileDragging(true); }}
          onDragLeave={(event) => { event.preventDefault(); setFileDragging(false); }}
          onDrop={(event) => {
            event.preventDefault();
            setFileDragging(false);
            receiveSpreadsheet(event.dataTransfer?.files?.[0]);
          }}
          className={`block border-2 border-dashed p-6 text-center transition-colors ${uploading
            ? "cursor-not-allowed border-border bg-muted/30 opacity-70"
            : fileDragging
              ? "cursor-copy border-primary bg-primary/10"
              : "cursor-pointer border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10"}`}
        >
          <input
            ref={fileRef}
            id="historical-exam-upload-input"
            type="file"
            className="sr-only"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={uploading || downloading}
            data-testid="historical-exam-upload-input"
            onChange={(event) => receiveSpreadsheet(event.target.files?.[0])}
          />
          <UploadSimple size={24} weight="bold" className="mx-auto mb-2 text-primary" />
          <div className="font-mono-data font-bold text-sm" data-testid="historical-exam-upload-btn">
            {uploading ? "Importando planilha…" : "Clique aqui para escolher o XLSX"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">ou arraste a planilha de prova histórica para esta área</div>
        </label>

        {result && (
          <div className="border border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5 p-4" data-testid="historical-exam-import-result">
            <div className="font-mono-data font-bold text-[hsl(var(--success))]">Prova histórica importada</div>
            <div className="text-sm mt-1">
              {result.question_count || 0} questão(ões) · {result.annulled_question_count || 0} anulada(s) · {result.result_count || 0} resultado(s) · {result.linked_students || 0} aluno(s) vinculado(s) · {result.created_questions || 0} questão(ões) criada(s)
            </div>
            {result.exam?.name && <div className="text-xs text-muted-foreground mt-2">{result.exam.name} · {result.semester?.name || "período histórico"}</div>}
          </div>
        )}

        {result && historicalLayoutClassification && (
          <div
            className={`border p-4 ${historicalClassificationIsWarning
              ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10"
              : historicalLayoutClassification.is_standard_tpi
                ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5"
                : "border-primary/40 bg-primary/5"}`}
            data-testid="historical-exam-layout-classification"
          >
            <div className="flex items-start gap-3">
              {historicalClassificationIsWarning
                ? <Warning size={22} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                : <BookOpen size={22} weight="fill" className="text-primary shrink-0 mt-0.5" />}
              <div className="min-w-0 text-sm">
                <div className="font-mono-data font-bold">
                  Classificacao do arquivo: {historicalLayoutClassification.label || "historico importado"}
                </div>
                <p className="text-muted-foreground mt-1">
                  {historicalLayoutClassification.message || "O tipo do cartao foi registrado junto com a prova historica."}
                </p>
                <div className="mt-2 text-xs font-mono-data text-muted-foreground">
                  {historicalLayoutClassification.objective_question_count || result.question_count || 0} questao(oes) objetivas
                  {" · "}{historicalLayoutClassification.selected_layout_name || result.layout_name || "tipo de gabarito informado"}
                </div>
                {historicalLayoutClassification.recommended_layout_name && (
                  <div className="mt-2 text-xs font-medium text-[hsl(var(--warning))]">
                    Tipo recomendado: {historicalLayoutClassification.recommended_layout_name}.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {result && importWarningCount > 0 && (
          <div role="alert" className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 p-4" data-testid="historical-exam-import-warnings">
            <div className="flex items-start gap-3">
              <Warning size={22} weight="fill" className="text-[hsl(var(--warning))] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-mono-data font-bold">{importWarningCount} aviso(s) de origem — itens salvos para revisão</div>
                <p className="text-sm text-muted-foreground mt-1">
                  A prova e os resultados foram importados, mas os itens abaixo foram preservados como <b>needs_review</b>. Revise-os antes de reutilizar a questão em uma nova prova.
                </p>
              </div>
            </div>
            {!!importWarnings.length && (
              <div className="mt-3 border border-[hsl(var(--warning))]/30 bg-card/60 max-h-52 overflow-y-auto divide-y divide-border text-sm font-mono-data">
                {importWarnings.slice(0, 20).map((warning, index) => (
                  <div key={`${index}-${typeof warning === "string" ? warning : warning?.message || warning?.detail || "aviso"}`} className="px-3 py-2">
                    {typeof warning === "string" ? warning : warning?.message || warning?.detail || "Inconsistência identificada na origem da questão."}
                  </div>
                ))}
                {(importWarnings.length > 20 || importWarningCount > importWarnings.length) && (
                  <div className="px-3 py-2 text-muted-foreground">… exibindo os primeiros {Math.min(20, importWarnings.length)} de {importWarningCount} aviso(s).</div>
                )}
              </div>
            )}
          </div>
        )}

        {!!errors.length && (
          <div className="border border-destructive/50 bg-destructive/5 p-4" data-testid="historical-exam-import-errors">
            <div className="font-mono-data font-bold text-destructive mb-2">Arquivo rejeitado — corrija as linhas abaixo</div>
            <div className="max-h-48 overflow-y-auto space-y-1 text-sm font-mono-data">
              {errors.slice(0, 100).map((error, index) => <div key={`${index}-${error}`}>{error}</div>)}
              {errors.length > 100 && <div>… e mais {errors.length - 100} erro(s)</div>}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-none" onClick={close} disabled={uploading}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Page ---------- */
export default function ExamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [bookletsOpen, setBookletsOpen] = useState(false);
  const [editingBookletGroup, setEditingBookletGroup] = useState(null);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [stages, setStages] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [creationContext, setCreationContext] = useState(null);
  const [filterClass, setFilterClass] = useState("all");
  const [filterSem, setFilterSem] = useState("all");
  const [onlyUnlinkedCpf, setOnlyUnlinkedCpf] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [newMode, setNewMode] = useState("key");
  const [openQuestionBuilder, setOpenQuestionBuilder] = useState(false);
  const [createForLive, setCreateForLive] = useState(false);
  const [openGenericCorrection, setOpenGenericCorrection] = useState(false);
  const [openHistoricalImport, setOpenHistoricalImport] = useState(false);
  const [newName, setNewName] = useState("");
  const [newExamDate, setNewExamDate] = useState(todayInputValue());
  const [newClasses, setNewClasses] = useState([]);
  const [newSchoolScopeId, setNewSchoolScopeId] = useState("");
  const [newSchoolWide, setNewSchoolWide] = useState(false);
  const [newAllowExceptionalClasses, setNewAllowExceptionalClasses] = useState(false);
  const [newSem, setNewSem] = useState("");
  const [newStage, setNewStage] = useState("");
  const [newAcademicScope, setNewAcademicScope] = useState("global");
  const [newDiscipline, setNewDiscipline] = useState("");
  const [newQuestionCount, setNewQuestionCount] = useState(100);
  const [newPerceptionCount, setNewPerceptionCount] = useState(9);
  const [newLayoutId, setNewLayoutId] = useState("saki-oficial-a4-100");
  const [selected, setSelected] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const [workspaceExam, setWorkspaceExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const requestedExamId = searchParams.get("open");
    if (!requestedExamId || !exams.length) return;
    const requestedExam = exams.find((item) => item.id === requestedExamId);
    if (!requestedExam || workspaceExam?.id === requestedExam.id) return;
    let cancelled = false;
    api.get(`/exams/${requestedExam.id}`)
      .then((response) => {
        if (!cancelled) setWorkspaceExam(response.data);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(fmtError(error));
          setSearchParams({});
        }
      });
    return () => { cancelled = true; };
  }, [exams, searchParams, workspaceExam?.id]);

  const load = async () => {
    try {
      const [schoolResponse, c, s, stageResponse, disciplineResponse, l, contextResponse] = await Promise.all([
        api.get("/schools"), api.get("/classes"), api.get("/semesters"), api.get("/academic-stages"),
        api.get("/disciplines"), api.get("/omr/layouts"), api.get("/exam-creation-context"),
      ]);
      setSchools(schoolResponse.data || []);
      setClasses(c.data);
      setSemesters(s.data);
      setNewSem((current) => current || (s.data || []).find((item) => item.is_current)?.id || "");
      setStages(stageResponse.data || []);
      setDisciplines(disciplineResponse.data || []);
      setLayouts(l.data.layouts || []);
      setCreationContext(contextResponse.data || null);
      setNewLayoutId((current) => current || l.data.default_layout_id || "saki-oficial-a4-100");
    } catch (e) {
      const message = fmtError(e);
      setLoadError(message);
      toast.error(message);
    }
  };
  const loadExams = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams();
      if (filterClass !== "all") params.set("class_id", filterClass);
      if (filterSem !== "all") params.set("semester_id", filterSem);
      const query = params.toString();
      const r = await api.get(query ? `/exams?${query}` : "/exams");
      setExams(r.data);
    } catch (e) {
      const message = fmtError(e);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [filterClass, filterSem]);
  useEffect(() => { load(); }, []);
  useEffect(() => { loadExams(); }, [loadExams]);

  const create = async () => {
    try {
      const selectedLayout = layouts.find((item) => item.id === newLayoutId);
      const res = await api.post("/exams", {
        name: newName,
        exam_date: newExamDate,
        class_ids: newClasses,
        school_id: newSchoolId || null,
        school_wide: Boolean(newAcademicScope === "global" && newSchoolWide),
        allow_exceptional_classes: Boolean(newAllowExceptionalClasses),
        semester_id: newSem,
        stage_id: newStage || null,
        academic_scope: newAcademicScope === "global" && newSchoolWide ? "global" : (newStage ? newAcademicScope : "legacy"),
        discipline_id: newStage && newAcademicScope === "discipline" ? newDiscipline : null,
        question_count: Number(newQuestionCount || 100),
        perception_count: Math.min(
          Number(newPerceptionCount || 0),
          selectedLayout?.max_perception_count ?? 10,
        ),
        layout_id: newLayoutId,
      });
      toast.success("Prova criada");
      setOpenNew(false);
      setNewName("");
      setNewExamDate(todayInputValue());
      setNewClasses([]);
      setNewSchoolScopeId("");
      setNewSchoolWide(false);
      setNewAllowExceptionalClasses(false);
      setNewSem("");
      setNewStage("");
      setNewAcademicScope("global");
      setNewDiscipline("");
      setNewQuestionCount(100);
      setNewPerceptionCount(9);
      setNewLayoutId("saki-oficial-a4-100");
      loadExams();
      setSelected(res.data);  // Open editor immediately
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remover a prova, o gabarito, todas as correções vinculadas e seus arquivos? Esta ação não pode ser desfeita.")) return;
    try { await api.delete(`/exams/${id}`); loadExams(); toast.success("Removida"); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/exams/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_gabarito.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const downloadGenericForm = async () => {
    try {
      const res = await api.get("/forms/base.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "cartao_padrao.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const downloadExamForm = async (exam) => {
    try {
      const res = await api.get(`/exams/${exam.id}/form.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `cartao_${exam.name || exam.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtError(e)); }
  };


  const downloadExamPaper = async (exam, includeKey = false) => {
    try {
      const res = await api.get(`/exams/${exam.id}/paper.pdf?include_key=${includeKey ? "true" : "false"}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `caderno_${exam.name || exam.id}${includeKey ? "_gabarito" : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtError(e)); }
  };

  const nameOf = (id, list) => list.find(x => x.id === id)?.name || "—";

  const newClassSchoolIds = Array.from(new Set(
    newClasses.map((id) => classes.find((item) => item.id === id)?.school_id).filter(Boolean),
  ));
  const newSchoolId = newSchoolScopeId || (newClassSchoolIds.length === 1 ? newClassSchoolIds[0] : "");
  const selectableNewSchools = (creationContext?.is_admin || !creationContext)
    ? schools
    : schools.filter((item) => {
      const id = item.id;
      return (creationContext.school_manager_school_ids || []).includes(id)
        || (creationContext.stage_manager_assignments || []).some((entry) => entry.school_id === id)
        || (creationContext.teacher_assignments || []).some((entry) => entry.school_id === id);
    });
  const teacherAssignments = creationContext?.teacher_assignments || [];
  const onlyTeacherAssignments = Boolean(
    creationContext && !creationContext.is_admin && !creationContext.can_create_global,
  );
  const teacherAssignmentCandidates = teacherAssignments.filter((entry) => (
    entry.semester_id === newSem
    && (!entry.school_id || entry.school_id === newSchoolId)
    && (!newStage || entry.stage_id === newStage)
  ));
  const teacherAssignmentForNew = newDiscipline
    ? teacherAssignmentCandidates.find((entry) => entry.discipline_id === newDiscipline)
    : (teacherAssignmentCandidates.length === 1 ? teacherAssignmentCandidates[0] : null);
  const teacherDefaultClassIds = (teacherAssignmentForNew?.class_ids || []).filter((id) => classes.some((item) => item.id === id));
  const teacherStageIds = new Set(teacherAssignments
    .filter((entry) => entry.semester_id === newSem && (!entry.school_id || entry.school_id === newSchoolId))
    .map((entry) => entry.stage_id)
    .filter(Boolean));
  const teacherStageClassIds = new Set(teacherAssignments
    .filter((entry) => entry.semester_id === newSem
      && (!entry.school_id || entry.school_id === newSchoolId)
      && (!newStage || entry.stage_id === newStage))
    .flatMap((entry) => entry.class_ids || []));
  const availableNewSemesters = semesters.filter((item) => !item.school_id || !newSchoolId || item.school_id === newSchoolId);
  const availableNewStages = stages.filter((item) => (item.matrix_id || item.semester_id === newSem)
    && (!newSchoolId || item.school_id === newSchoolId)
    && (!onlyTeacherAssignments || teacherStageIds.has(item.id))
    && (onlyTeacherAssignments || !newClasses.length || newClasses.every((id) => (item.class_ids || []).includes(id))));
  const availableNewDisciplines = disciplines.filter((item) => item.stage_id === newStage
    && (!onlyTeacherAssignments || teacherAssignments.some((entry) => (
      entry.discipline_id === item.id
      && entry.semester_id === newSem
      && (!entry.school_id || entry.school_id === newSchoolId)
    ))));
  const canCreateGlobal = Boolean(creationContext?.is_admin || creationContext?.can_create_global);
  const canCreateSchoolWide = Boolean(creationContext?.is_admin || creationContext?.can_create_school_wide);
  const availableNewClasses = classes.filter((item) => (
    (!newSchoolId || item.school_id === newSchoolId)
    && (!onlyTeacherAssignments || !newStage || !teacherStageClassIds.size || teacherStageClassIds.has(item.id))
    && (!onlyTeacherAssignments || newAllowExceptionalClasses || !newDiscipline || teacherDefaultClassIds.includes(item.id))
  ));

  useEffect(() => {
    if (!onlyTeacherAssignments || newSchoolScopeId) return;
    const teacherSchoolIds = [...new Set(teacherAssignments.map((entry) => entry.school_id).filter(Boolean))];
    if (teacherSchoolIds.length === 1) setNewSchoolScopeId(teacherSchoolIds[0]);
  }, [onlyTeacherAssignments, newSchoolScopeId, teacherAssignments]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !newSchoolId) return;
    const offeredSemesterIds = [...new Set(teacherAssignments
      .filter((entry) => !entry.school_id || entry.school_id === newSchoolId)
      .map((entry) => entry.semester_id)
      .filter(Boolean))];
    if (!offeredSemesterIds.length || offeredSemesterIds.includes(newSem)) return;
    const currentOffered = semesters.find((item) => item.is_current && offeredSemesterIds.includes(item.id));
    setNewSem(currentOffered?.id || offeredSemesterIds[0]);
  }, [onlyTeacherAssignments, newSchoolId, newSem, teacherAssignments, semesters]);

  useEffect(() => {
    if (newSem && !availableNewSemesters.some((item) => item.id === newSem)) {
      setNewSem(""); setNewStage(""); setNewDiscipline("");
    }
  }, [newSchoolId, newSem, semesters]);

  useEffect(() => {
    if (newStage && !availableNewStages.some((item) => item.id === newStage)) {
      setNewStage(""); setNewDiscipline("");
    }
  }, [newSem, newStage, newClasses, stages]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !newSem || !newSchoolId || newStage || availableNewStages.length !== 1) return;
    setNewStage(availableNewStages[0].id);
    setNewAcademicScope("discipline");
  }, [onlyTeacherAssignments, newSem, newSchoolId, newStage, availableNewStages]);

  useEffect(() => {
    if (!onlyTeacherAssignments || !newStage || newDiscipline || availableNewDisciplines.length !== 1) return;
    setNewDiscipline(availableNewDisciplines[0].id);
    setNewAcademicScope("discipline");
  }, [onlyTeacherAssignments, newStage, newDiscipline, availableNewDisciplines]);

  useEffect(() => {
    if (!onlyTeacherAssignments || newAllowExceptionalClasses || !newDiscipline || !teacherAssignmentForNew) return;
    setNewClasses((current) => (
      current.length === teacherDefaultClassIds.length
      && current.every((id) => teacherDefaultClassIds.includes(id))
        ? current
        : teacherDefaultClassIds
    ));
  }, [onlyTeacherAssignments, newAllowExceptionalClasses, newDiscipline, teacherAssignmentForNew, teacherDefaultClassIds]);

  useEffect(() => {
    if (!canCreateGlobal && newAcademicScope !== "discipline") {
      setNewAcademicScope("discipline");
      setNewSchoolWide(false);
    }
  }, [canCreateGlobal, newAcademicScope]);

  const groupedExams = useMemo(() => {
    const semesterMap = new Map();
    bookletRows(exams.filter((exam) => !onlyUnlinkedCpf || Number(exam.unlinked_student_cards || 0) > 0)).forEach((exam) => {
      const semesterName = nameOf(exam.semester_id, semesters);
      if (!semesterMap.has(exam.semester_id)) {
        semesterMap.set(exam.semester_id, { id: exam.semester_id, name: semesterName, exams: [] });
      }
      semesterMap.get(exam.semester_id).exams.push(exam);
    });
    return Array.from(semesterMap.values())
      .map((semester) => ({
        ...semester,
        exams: semester.exams.sort((a, b) => String(b.exam_date || b.created_at || "").localeCompare(String(a.exam_date || a.created_at || ""))),
      }))
      .sort((a, b) => b.name.localeCompare(a.name, "pt-BR", { numeric: true }));
  }, [exams, semesters, onlyUnlinkedCpf]);

  const toggleNewClass = (id) => {
    setNewClasses((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const handleExamChange = (updated) => {
    setExams((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
    setWorkspaceExam((current) => current?.id === updated.id ? updated : current);
    setSelected(updated);
    if (updated.booklet_keys_updated) loadExams();
  };

  const openWorkspace = (exam) => {
    setSearchParams({ open: exam.id });
  };

  const closeWorkspace = () => {
    setWorkspaceExam(null);
    setSearchParams({});
  };

  const detailDialog = selected ? (
    <ExamDetail
      exam={selected}
      layouts={layouts}
      classes={classes}
      semesters={semesters}
      stages={stages}
      disciplines={disciplines}
      onClose={() => setSelected(null)}
      onChange={handleExamChange}
    />
  ) : null;

  const genericDialog = (
    <GenericCorrectionDialog
      open={openGenericCorrection}
      onOpenChange={setOpenGenericCorrection}
    />
  );

  const copyDialog = copySource ? (
    <ExamCopyDialog
      key={copySource.id}
      exam={copySource}
      schools={schools}
      classes={classes}
      semesters={semesters}
      disciplines={disciplines}
      creationContext={creationContext}
      onClose={() => setCopySource(null)}
      onCopied={(exam) => {
        setExams((current) => [exam, ...current.filter((item) => item.id !== exam.id)]);
        setFilterClass("all");
        setFilterSem("all");
        setOnlyUnlinkedCpf(false);
        setWorkspaceExam(exam);
        setSearchParams({ open: exam.id });
      }}
    />
  ) : null;

  const bookletsDialog = <ExamBookletsDialog open={bookletsOpen} onClose={() => setBookletsOpen(false)} exams={exams} group={editingBookletGroup} onSaved={loadExams} />;
  if (workspaceExam) {
    const workspaceGroup = exams.find(item => item.id === workspaceExam.id)?.booklet_group;
    const workspaceExamClassIds = (workspaceExam.class_ids || [workspaceExam.class_id]).filter(Boolean);
    // The reviewed TPI historical exams deliberately have no permanent class
    // association. A teacher can still choose one of their authorized classes
    // for a single card-scanning batch; the backend uses it only as the
    // temporary name-OCR roster and never mutates this exam.
    const workspaceClassOptions = workspaceExam.historical_unassigned
      ? classes
      : classes.filter((item) => workspaceExamClassIds.includes(item.id));
    return (
      <>
        {bookletsDialog}
        {workspaceGroup && <BookletNavigation group={workspaceGroup} examId={workspaceExam.id}
          onSelect={id => openWorkspace(exams.find(item => item.id === id))}
          onManage={group => { setEditingBookletGroup(group); setBookletsOpen(true); }} />}
        {workspaceExam.can_copy && <div className="max-w-[1400px] mx-auto px-8 pt-5 flex justify-end">
          <Button variant="outline" className="rounded-none" data-testid="workspace-copy-exam" onClick={() => setCopySource(workspaceExam)}><Copy className="mr-2" />Copiar prova</Button>
        </div>}
        <ExamWorkspace
          key={workspaceExam.id}
          exam={workspaceExam}
          bookletGroup={workspaceGroup}
          classNames={workspaceExamClassIds.map((id) => nameOf(id, classes))}
          classOptions={workspaceClassOptions}
          semesterName={[
            nameOf(workspaceExam.semester_id, semesters),
            workspaceExam.stage_id ? nameOf(workspaceExam.stage_id, stages) : "",
            workspaceExam.stage_id
              ? (workspaceExam.academic_scope === "discipline" ? nameOf(workspaceExam.discipline_id, disciplines) : "Global / simulado")
              : "",
          ].filter(Boolean).join(" · ")}
          initialTab={searchParams.get("tab") || (workspaceExam.delivery_mode === "online_only" ? "results" : "upload")}
          onBack={closeWorkspace}
          onEdit={() => setSelected(workspaceExam)}
          onExamChanged={handleExamChange}
        />
        {detailDialog}
        {genericDialog}
        {copyDialog}
      </>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      {bookletsDialog}
      <Button variant="outline" className="mb-5" onClick={() => { setEditingBookletGroup(null); setBookletsOpen(true); }}>Organizar versões em uma prova</Button>
      <div className="mb-8 border-b border-border pb-6">
        <div className="label-eyebrow mb-2">provas · gabaritos oficiais</div>
        <h1 className="font-display text-5xl font-black tracking-tighter leading-none">
          Provas.
        </h1>
        <p className="text-muted-foreground mt-3 text-sm max-w-[520px]">
          Cadastre a prova, configure o gabarito e abra sua área de trabalho para enviar cartões, conferir acertos e analisar as questões de percepção.
        </p>
      </div>


      {loadError && (
        <div
          role="alert"
          data-testid="exams-load-error"
          className="border border-destructive bg-destructive/5 p-4 mb-6 text-sm"
        >
          <div className="font-mono-data font-semibold text-destructive">Não foi possível carregar as provas.</div>
          <div className="text-muted-foreground mt-1">{loadError}</div>
          <Button
            variant="outline"
            onClick={() => { load(); loadExams(); }}
            className="rounded-none mt-3 font-mono-data uppercase tracking-widest text-xs"
          >
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Template + New */}
      <div className="border border-border bg-card p-6 mb-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="label-eyebrow mb-1">modelo do gabarito</div>
          <div className="text-sm text-muted-foreground max-w-[520px]">
            Crie uma prova somente com gabarito ou escolha “Com questões” para pesquisar, criar e editar itens durante a própria montagem.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="exams-template-download"
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
          >
            <DownloadSimple size={14} weight="bold" className="mr-2" /> Modelo XLSX
          </Button>
          <Button
            variant="outline"
            onClick={downloadGenericForm}
            className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
          >
            <DownloadSimple size={14} weight="bold" className="mr-2" /> Cartão padrão
          </Button>
          <Button
            variant="outline"
            onClick={() => setOpenGenericCorrection(true)}
            className="rounded-none border-border font-mono-data uppercase tracking-widest text-xs"
          >
            <FileArrowUp size={14} weight="bold" className="mr-2" /> Correção por QR
          </Button>
          <Button
            data-testid="new-live-exam-btn"
            variant="outline"
            onClick={() => { setCreateForLive(true); setOpenQuestionBuilder(true); }}
            className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
          >
            <PlayCircle size={14} weight="fill" className="mr-2" /> Nova prova online / ao vivo
          </Button>
          <Button
            type="button"
            data-testid="historical-exam-import-btn"
            variant="outline"
            onClick={(event) => { event.preventDefault(); setOpenHistoricalImport(true); }}
            className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"
          >
            <FileArrowUp size={14} weight="bold" className="mr-2" /> Importar histórica
          </Button>
          <Dialog open={openNew} onOpenChange={(value) => {
            setOpenNew(value);
            if (value) {
              setNewMode("key");
              setNewSem((current) => current || semesters.find((item) => item.is_current)?.id || "");
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="new-exam-btn" onClick={() => setCreateForLive(false)} className="rounded-none font-mono-data uppercase tracking-widest text-xs">
                <Plus size={14} weight="bold" className="mr-2" /> Nova prova
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto overscroll-contain" aria-describedby="new-exam-desc">
              <DialogHeader><DialogTitle className="font-display text-3xl font-black">Nova prova</DialogTitle></DialogHeader>
              <p id="new-exam-desc" className="text-sm text-muted-foreground">Escolha como deseja construir a prova.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button type="button" onClick={() => setNewMode("key")} className={`border p-4 text-left ${newMode === "key" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="font-mono-data font-bold">Somente gabarito</div>
                  <div className="text-xs text-muted-foreground mt-1">Cadastre o número de questões e marque apenas as respostas corretas.</div>
                </button>
                <button type="button" onClick={() => setNewMode("questions")} className={`border p-4 text-left ${newMode === "questions" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="font-mono-data font-bold">Com questões do banco</div>
                  <div className="text-xs text-muted-foreground mt-1">Pesquise, selecione, crie e edite questões dentro da montagem.</div>
                </button>
              </div>

              {newMode === "key" ? (
                <div className="space-y-3">
                  <Input placeholder="Nome (ex: Simulado 1)" value={newName} onChange={e => setNewName(e.target.value)} data-testid="new-exam-name" className="rounded-none" />
                  <div>
                    <div className="label-eyebrow mb-1">data de realização</div>
                    <Input type="date" value={newExamDate} onChange={(e) => setNewExamDate(e.target.value)} data-testid="new-exam-date" className="rounded-none" />
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">escola</div>
                    <Select value={newSchoolId} onValueChange={(value) => { setNewSchoolScopeId(value); setNewClasses([]); setNewStage(""); setNewDiscipline(""); setNewAllowExceptionalClasses(false); }}>
                      <SelectTrigger data-testid="new-exam-school" className="rounded-none"><SelectValue placeholder="Selecione a escola..." /></SelectTrigger>
                      <SelectContent>{selectableNewSchools.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">semestre</div>
                    <Select value={newSem} onValueChange={setNewSem}>
                      <SelectTrigger data-testid="new-exam-semester" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{availableNewSemesters.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " · atual" : s.semester_status === "future" ? " · futuro" : s.semester_status === "history" ? " · histórico" : " · global"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">etapa</div>
                    <Select value={newStage || "none"} onValueChange={(value) => { setNewStage(value === "none" ? "" : value); setNewAcademicScope(onlyTeacherAssignments ? "discipline" : "global"); setNewDiscipline(""); setNewClasses([]); setNewAllowExceptionalClasses(false); }}>
                      <SelectTrigger data-testid="new-exam-stage" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {!onlyTeacherAssignments && <SelectItem value="none">Sem etapa / prova legada</SelectItem>}
                        {availableNewStages.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!availableNewStages.length && newSem && <div className="text-xs text-muted-foreground mt-1">Nenhuma etapa disponível para a escola e semestre selecionados.</div>}
                  </div>
                  {newStage && (
                    <div>
                      <div className="label-eyebrow mb-1">tipo acadêmico</div>
                      <Select value={newAcademicScope} onValueChange={(value) => { setNewAcademicScope(value); if (value === "global") setNewDiscipline(""); }}>
                        <SelectTrigger data-testid="new-exam-scope" className="rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {canCreateGlobal && <SelectItem value="global">Global da etapa / simulado</SelectItem>}
                          <SelectItem value="discipline">Prova de disciplina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newStage && newAcademicScope === "discipline" && (
                    <div>
                      <div className="label-eyebrow mb-1">disciplina</div>
                      <Select value={newDiscipline} onValueChange={setNewDiscipline}>
                        <SelectTrigger data-testid="new-exam-discipline" className="rounded-none"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>{availableNewDisciplines.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {onlyTeacherAssignments && newStage && newAcademicScope === "discipline" && newDiscipline && teacherAssignmentForNew && (
                    <label className="flex gap-3 border border-border bg-muted/30 p-3 text-sm">
                      <input
                        type="checkbox"
                        checked={newAllowExceptionalClasses}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setNewAllowExceptionalClasses(enabled);
                          if (!enabled) setNewClasses(teacherDefaultClassIds);
                        }}
                      />
                      <span><b>Escolher excepcionalmente outra turma</b><br /><span className="text-muted-foreground">Por padrão, já estão marcadas as turmas da sua disciplina neste semestre.</span></span>
                    </label>
                  )}
                  {newAcademicScope === "global" && canCreateSchoolWide && (
                    <label className="flex gap-3 border border-primary bg-primary/5 p-3 text-sm">
                      <input type="checkbox" checked={newSchoolWide} onChange={(event) => { setNewSchoolWide(event.target.checked); if (event.target.checked) setNewClasses([]); }} />
                      <span><b>Aplicar à escola toda</b><br /><span className="text-muted-foreground">Inclui todas as turmas da escola e habilita a comparação por etapa e área.</span></span>
                    </label>
                  )}
                  <div>
                    <div className="label-eyebrow mb-2">turmas da prova <span className="normal-case text-muted-foreground">{onlyTeacherAssignments && !newAllowExceptionalClasses ? "(da disciplina, pré-selecionadas)" : "(opcional)"}</span></div>
                    <div className="border border-border p-3 grid gap-2 max-h-52 overflow-y-auto" data-testid="new-exam-classes">
                      {availableNewClasses.map((classItem) => (
                        <label key={classItem.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" disabled={newSchoolWide} checked={newClasses.includes(classItem.id)} onChange={() => toggleNewClass(classItem.id)} />
                          <span>{classItem.name}{classItem.school_id ? ` · ${nameOf(classItem.school_id, schools)}` : ""}</span>
                        </label>
                      ))}
                      {!availableNewClasses.length && <div className="text-xs text-muted-foreground">Selecione a disciplina para carregar as turmas configuradas.</div>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono-data">{newSchoolWide ? "todas as turmas da escola" : `${newClasses.length} turma(s) selecionada(s)`}</div>
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1">layout do cartão</div>
                    <Select
                      value={newLayoutId}
                      onValueChange={(value) => {
                        const layout = layouts.find((item) => item.id === value);
                        setNewLayoutId(value);
                        if (layout) {
                          setNewQuestionCount(layout.default_question_count);
                          setNewPerceptionCount(layout.default_perception_count);
                        }
                      }}
                    >
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{layouts.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="text-xs text-muted-foreground mt-1">
                      {layouts.find((item) => item.id === newLayoutId)?.description}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><div className="label-eyebrow mb-1">questões objetivas visíveis</div><Input type="number" min={1} max={layouts.find((item) => item.id === newLayoutId)?.max_question_count || 100} value={newQuestionCount} onChange={(e) => setNewQuestionCount(e.target.value)} className="rounded-none" /></div>
                    <div><div className="label-eyebrow mb-1">questões de percepção visíveis</div><Input type="number" min={0} max={layouts.find((item) => item.id === newLayoutId)?.max_perception_count ?? 10} disabled={(layouts.find((item) => item.id === newLayoutId)?.max_perception_count ?? 10) === 0} value={newPerceptionCount} onChange={(e) => setNewPerceptionCount(e.target.value)} className="rounded-none" />{(layouts.find((item) => item.id === newLayoutId)?.max_perception_count ?? 10) === 0 && <div className="text-xs text-muted-foreground mt-1">Este cartão não possui campos físicos de percepção.</div>}</div>
                  </div>
                </div>
              ) : (
                <div className="border border-primary bg-primary/5 p-5">
                  <div className="font-semibold">Montagem completa</div>
                  <div className="text-sm text-muted-foreground mt-1">Na próxima tela você informará turma e semestre, buscará questões por área e sistema, organizará a ordem, criará novas questões e editará as permitidas pela hierarquia.</div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setOpenNew(false)}>Cancelar</Button>
                {newMode === "key" ? (
                  <Button className="rounded-none" onClick={create} disabled={!newName || !newExamDate || !newSchoolId || !newSem || (onlyTeacherAssignments && !newStage) || (newStage && newAcademicScope === "discipline" && !newDiscipline)} data-testid="new-exam-save">Criar somente gabarito</Button>
                ) : (
                  <Button className="rounded-none" onClick={() => { setCreateForLive(false); setOpenNew(false); setOpenQuestionBuilder(true); }}>Abrir montagem de questões</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="label-eyebrow mb-1">turma</div>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger data-testid="exam-filter-class" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as turmas</SelectItem>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-eyebrow mb-1">semestre</div>
          <Select value={filterSem} onValueChange={setFilterSem}>
            <SelectTrigger data-testid="exam-filter-sem" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os semestres</SelectItem>
              {semesters.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <button
        type="button"
        data-testid="exam-filter-unlinked-cpf"
        onClick={() => setOnlyUnlinkedCpf((value) => !value)}
        className={`mb-6 px-4 py-3 border text-left text-sm font-mono-data transition-colors ${onlyUnlinkedCpf ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 text-foreground" : "border-border bg-card text-muted-foreground hover:border-foreground"}`}
      >
        <span className="font-bold">{onlyUnlinkedCpf ? "✓ " : ""}Somente provas com CPF sem aluno</span>
        <span className="ml-2 text-xs">Localiza também importações antigas que ainda precisam de vínculo.</span>
      </button>

      {/* Exams grouped by semester */}
      <div className="space-y-7">
        {loading ? (
          <div className="border border-border bg-card p-16 text-center label-eyebrow">carregando provas…</div>
        ) : groupedExams.length === 0 ? (
          <div className="border border-border bg-card p-16 text-center label-eyebrow">
            {onlyUnlinkedCpf ? "nenhuma prova com CPF sem aluno" : "nenhuma prova cadastrada"}
          </div>
        ) : (
          groupedExams.map((semesterGroup) => (
            <section key={semesterGroup.id} className="border border-border bg-card">
              <div className="px-6 py-5 border-b border-border bg-muted/10 flex items-center justify-between gap-4">
                <div>
                  <div className="label-eyebrow">semestre</div>
                  <div className="font-display text-2xl font-black tracking-tight">{semesterGroup.name}</div>
                </div>
                <div className="font-mono-data text-xs text-muted-foreground">
                  {semesterGroup.exams.length} prova(s)
                </div>
              </div>
              <div className="divide-y divide-border">
                {semesterGroup.exams.map((examItem) => {
                  const marked = Object.values(examItem.answers || {}).filter(Boolean).length;
                  const examClassIds = (examItem.class_ids || [examItem.class_id]).filter(Boolean);
                  const examClassNames = examClassIds.map((id) => nameOf(id, classes));
                  const stageName = examItem.stage_id ? nameOf(examItem.stage_id, stages) : "";
                  const disciplineName = examItem.discipline_id ? nameOf(examItem.discipline_id, disciplines) : "";
                  const isHistoricalExam = Boolean(
                    examItem.is_historical
                    || examItem.historical_import
                    || examItem.import_kind === "historical"
                    || examItem.origin === "historical_import",
                  );
                  return (
                    <div key={examItem.id} data-testid={`exam-row-${examItem.id}`}
                      className="grid grid-cols-1 lg:grid-cols-[minmax(0,60%)_minmax(0,1fr)] lg:items-center gap-4 px-6 py-4 hover:bg-muted/20 transition-colors">
                      <div data-testid={`exam-summary-${examItem.id}`} className="flex items-center gap-4 min-w-0 lg:pr-4">
                        <BookOpen size={22} weight="duotone" className="text-primary shrink-0" />
                        <div className="min-w-0">
                          <div data-testid={`exam-name-${examItem.id}`} className="font-mono-data font-semibold whitespace-normal break-words">{examItem.booklet_group?.name || examItem.name}</div>
                          {examItem.booklet_group && <div className="text-sm mt-2">{examItem.booklet_group.members.length} cadernos · tipos {examItem.booklet_group.members.map(m => m.number).join(", ")}</div>}
                          <div className="text-xs text-primary mt-1 truncate">{examClassNames.join(" · ")}</div>
                          {stageName && (
                            <div className="text-xs font-mono-data mt-1">
                              {stageName} · {examItem.academic_scope === "discipline" ? disciplineName : "Prova global / simulado"}
                            </div>
                          )}
                          <div className="text-xs text-foreground font-mono-data mt-1">Realização: {examItem.exam_date ? new Date(`${examItem.exam_date}T12:00:00`).toLocaleDateString("pt-BR") : "data não informada"}</div>
                          {isHistoricalExam && (
                            <div className="inline-flex mt-2 px-2 py-1 border border-primary/50 bg-primary/5 text-primary text-[10px] font-mono-data font-bold uppercase tracking-widest">
                              histórico · importada por XLSX
                            </div>
                          )}
                          {Number(examItem.unlinked_student_cards || 0) > 0 && (
                            <div className="inline-flex mt-2 px-2 py-1 border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 text-[11px] font-mono-data font-bold">
                              {`${examItem.unlinked_cpf_count || examItem.unlinked_student_cards} CPF(s) sem aluno · ${examItem.unlinked_student_cards} cartão(ões)`}
                            </div>
                          )}
                          {(Number(examItem.blank_card_count || 0) > 0 || Number(examItem.incomplete_card_count || 0) > 0) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {Number(examItem.blank_card_count || 0) > 0 && <span className="px-2 py-1 border border-destructive bg-destructive/5 text-destructive text-[11px] font-mono-data font-bold">{examItem.blank_card_count} cartão(ões) vazio(s)</span>}
                              {Number(examItem.incomplete_card_count || 0) > 0 && <span className="px-2 py-1 border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10 text-[11px] font-mono-data font-bold">{examItem.incomplete_card_count} com muitas respostas faltantes</span>}
                            </div>
                          )}
                          {Number(examItem.pending_question_key_changes || 0) > 0 && (
                            <div className="inline-flex mt-2 px-2 py-1 border border-destructive bg-destructive/5 text-destructive text-[11px] font-mono-data font-bold">
                              {examItem.pending_question_key_changes} mudança(s) de gabarito aguardando decisão
                            </div>
                          )}
                          {!examItem.can_write && <div className="inline-flex mt-2 px-2 py-1 border border-primary/40 text-primary text-[10px] font-mono-data uppercase tracking-widest">compartilhada · {examItem.user_access_level === "individual" ? "resultados individuais" : examItem.user_access_level === "global" ? "resultados globais" : "somente prova"}</div>}
                          <div className="text-xs text-muted-foreground font-mono-data mt-1">
                            {examItem.question_source === "question_bank" ? "Banco de questões" : "Somente gabarito"} · {marked}/{examItem.question_count || 100} respostas no gabarito · {examItem.delivery_mode === "online_only"
                              ? `aplicação exclusivamente online · ${examItem.question_count || 100} questões`
                              : `cartão Q${examItem.question_count || 100}/P${examItem.perception_count ?? 9} · ${layouts.find((item) => item.id === examItem.layout_id)?.name || examItem.layout_id || "SAKI/Xm A4 · 100"}`}
                          </div>
                        </div>
                      </div>
                      <div data-testid={`exam-actions-${examItem.id}`} className="flex min-w-0 flex-wrap items-center justify-end gap-2 lg:self-center">
                        <Button data-testid={`exam-open-${examItem.id}`} onClick={() => openWorkspace(examItem)}
                          className="rounded-none font-mono-data uppercase tracking-widest text-xs">Abrir prova</Button>
                        {!examItem.booklet_group && <><ExamQuickPublishButton
                          exam={examItem}
                          onPublished={(publication) => setExams((current) => current.map((item) => (
                            item.id === examItem.id ? { ...item, ...publication } : item
                          )))}
                        />
                        {examItem.can_copy && <Button data-testid={`exam-copy-${examItem.id}`} variant="outline" onClick={() => setCopySource(examItem)} className="rounded-none font-mono-data uppercase tracking-widest text-xs"><Copy className="mr-2" />Copiar prova</Button>}
                        {examItem.question_source === "question_bank" && <>
                          {examItem.can_write && <Button data-testid={`exam-live-${examItem.id}`} variant="outline" onClick={() => navigate(`/exams/${examItem.id}/live`)} className="rounded-none border-primary text-primary font-mono-data uppercase tracking-widest text-xs"><PlayCircle className="mr-2" weight="fill" />Aplicar / acompanhar</Button>}
                          <Button variant="outline" onClick={() => downloadExamPaper(examItem, false)} className="rounded-none font-mono-data uppercase tracking-widest text-xs">Caderno</Button>
                          <Button variant="outline" onClick={() => downloadExamPaper(examItem, true)} className="rounded-none font-mono-data uppercase tracking-widest text-xs">Comentado</Button>
                        </>}
                        {examItem.delivery_mode !== "online_only" && layouts.find((item) => item.id === examItem.layout_id)?.printable !== false && (
                          <Button variant="outline" onClick={() => downloadExamForm(examItem)} className="rounded-none font-mono-data uppercase tracking-widest text-xs">Cartão</Button>
                        )}
                        {examItem.can_write && <Button data-testid={`exam-edit-${examItem.id}`} variant="outline" onClick={() => setSelected(examItem)} className="rounded-none font-mono-data uppercase tracking-widest text-xs">{examItem.question_source === "question_bank" ? "Editar" : "Gabarito"}</Button>}
                        {examItem.can_write && <button data-testid={`exam-delete-${examItem.id}`} onClick={() => del(examItem.id)}
                          className="text-muted-foreground hover:text-destructive p-2">
                          <Trash size={14} weight="bold" />
                        </button>}
                        </>}
                        {examItem.booklet_group && examItem.can_write && <Button variant="outline" onClick={() => { setEditingBookletGroup(examItem.booklet_group); setBookletsOpen(true); }}>Organizar cadernos</Button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      <ExamQuestionBuilder
        open={openQuestionBuilder}
        onOpenChange={setOpenQuestionBuilder}
        classes={classes}
        schools={schools}
        semesters={semesters}
        stages={stages}
        disciplines={disciplines}
        layouts={layouts}
        creationContext={creationContext}
        onCreated={(exam) => {
          setExams((current) => [exam, ...current.filter((item) => item.id !== exam.id)]);
          if (createForLive) {
            setCreateForLive(false);
            navigate(`/exams/${exam.id}/live`);
            return;
          }
          setWorkspaceExam(exam);
          setSearchParams({ open: exam.id });
        }}
      />
      <HistoricalExamImportDialog
        open={openHistoricalImport}
        onOpenChange={setOpenHistoricalImport}
        classes={classes}
        semesters={semesters}
        layouts={layouts}
        onImported={(payload) => {
          if (payload?.exam) {
            setExams((current) => [payload.exam, ...current.filter((item) => item.id !== payload.exam.id)]);
          }
          if (payload?.semester) {
            setSemesters((current) => current.some((item) => item.id === payload.semester.id)
              ? current.map((item) => item.id === payload.semester.id ? payload.semester : item)
              : [...current, payload.semester]);
          }
          loadExams();
        }}
      />
      {detailDialog}
      {genericDialog}
      {copyDialog}
    </div>
  );
}
